import { useQuery } from '@tanstack/react-query'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { fetchLumpsumAllocationPlanRecommendation } from '../../api/repository'
import { LUMPSUM_PLANS_API_RETRY_COUNT, isLumpsumQueryFetchFailed, isLumpsumQueryLoading } from '../../../api/queryOptions'
import type {
  LumpsumAllocationBuyPlanRecommendationDetails,
  LumpsumAllocationPlanRecommendationApiResponse
} from '../../api/allocationRecommendation.types'
import type { FundMissedGainsInfoParams } from '../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { AllocationBuyPlanRecommendationSheet } from './AllocationBuyPlanRecommendationSheet'

const ALLOCATION_BUY_PLAN_RECOMMENDATION_QUERY_KEY = 'lumpsum-plans-v4-allocation-buy-recommendation'

export function AllocationBuyPlanRecommendationContainer(input: {
  onDismiss: () => void
  isin: string
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  transactionCta: unknown
}) {
  const { onDismiss, isin, onMissedGainsInfoClick, transactionCta } = input
  const { planDetails, isLoading, isFetchFailed, onRetry } = useAllocationBuyPlanRecommendation(isin, true)

  return (
    <AllocationBuyPlanRecommendationSheet
      onDismiss={onDismiss}
      isin={isin}
      planDetails={planDetails}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      onRetry={onRetry}
      onMissedGainsInfoClick={onMissedGainsInfoClick}
      transactionCta={transactionCta}
    />
  )
}

function useAllocationBuyPlanRecommendation(isin: string, enabled: boolean): {
  planDetails: LumpsumAllocationBuyPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
} {
  const query = useQuery({
    queryKey: [ALLOCATION_BUY_PLAN_RECOMMENDATION_QUERY_KEY, isin],
    queryFn: () =>
      fetchLumpsumAllocationPlanRecommendation(isin, LumpsumRecommendationActionType.BUY),
    enabled: enabled && isin.length > 0,
    retry: LUMPSUM_PLANS_API_RETRY_COUNT
  })
  const response = query.data ?? null

  return {
    planDetails: isBuyPlanRecommendationDetails(response) ? response : null,
    isLoading: isLumpsumQueryLoading(query),
    isFetchFailed: isLumpsumQueryFetchFailed(query),
    onRetry: () => {
      void query.refetch()
    }
  }
}

function isBuyPlanRecommendationDetails(
  response: LumpsumAllocationPlanRecommendationApiResponse | null
): response is LumpsumAllocationBuyPlanRecommendationDetails {
  return response != null && 'fundsToBeSold' in response
}
